/**
 * Color Scheme Presets
 * 
 * Predefined color schemes that users can choose from.
 * Each scheme works in both light and dark modes.
 */

export interface ColorScheme {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    accent?: string;
  };
}

export const colorSchemes: ColorScheme[] = [
  {
    id: 'custom',
    name: 'Custom',
    description: 'Define your own colors',
    colors: {
      primary: '#2E86AB',
      secondary: '#A23B72',
    },
  },
  {
    id: 'endurion',
    name: 'Endurion',
    description: 'Deep maroon and charcoal with a sharp red accent (default)',
    colors: {
      // Deep maroon carries interactive surfaces (buttons, toggles, selections)
      // so they read as colored, not grey. Bright red stays a sharp, sparse
      // accent (secondary); steel grey is the neutral support tone.
      primary: '#8A2C34',
      secondary: '#FF2332',
      accent: '#8A9099',
    },
  },
  {
    id: 'default',
    name: 'Ocean blue',
    description: 'Professional blue and purple theme',
    colors: {
      primary: '#2E86AB',
      secondary: '#A23B72',
    },
  },
  {
    id: 'forest',
    name: 'Forest green',
    description: 'Natural green and earth tones',
    colors: {
      primary: '#2D6A4F',
      secondary: '#52B788',
      accent: '#74C69D',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset orange',
    description: 'Warm orange and coral colors',
    colors: {
      primary: '#E76F51',
      secondary: '#F4A261',
      accent: '#E9C46A',
    },
  },
  {
    id: 'twilight',
    name: 'Twilight purple',
    description: 'Deep purple and violet theme',
    colors: {
      primary: '#6A4C93',
      secondary: '#9B5DE5',
      accent: '#C77DFF',
    },
  },
  {
    id: 'crimson',
    name: 'Crimson red',
    description: 'Bold red and burgundy theme',
    colors: {
      primary: '#C1121F',
      secondary: '#780000',
      accent: '#FDF0D5',
    },
  },
  {
    id: 'slate',
    name: 'Slate gray',
    description: 'Neutral gray and blue-gray theme',
    colors: {
      primary: '#475569',
      secondary: '#64748B',
      accent: '#94A3B8',
    },
  },
  {
    id: 'teal',
    name: 'Teal cyan',
    description: 'Fresh teal and cyan colors',
    colors: {
      primary: '#0891B2',
      secondary: '#06B6D4',
      accent: '#22D3EE',
    },
  },
  {
    id: 'amber',
    name: 'Amber gold',
    description: 'Warm amber and golden tones',
    colors: {
      primary: '#D97706',
      secondary: '#F59E0B',
      accent: '#FBBF24',
    },
  },
  {
    id: 'indigo',
    name: 'Indigo night',
    description: 'Deep indigo and blue theme',
    colors: {
      primary: '#4338CA',
      secondary: '#6366F1',
      accent: '#818CF8',
    },
  },
  {
    id: 'rose',
    name: 'Rose pink',
    description: 'Elegant rose and pink theme',
    colors: {
      primary: '#E11D48',
      secondary: '#FB7185',
      accent: '#FDA4AF',
    },
  },
];

/**
 * Get a color scheme by ID
 */
export const getColorScheme = (id: string): ColorScheme => {
  return colorSchemes.find(scheme => scheme.id === id) || colorSchemes[0];
};

/**
 * Helper function to lighten a color (for light mode variations)
 */
export const lightenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1).toUpperCase();
};

/**
 * Helper function to darken a color (for dark mode variations)
 */
export const darkenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return '#' + (
    0x1000000 +
    (R > 0 ? R : 0) * 0x10000 +
    (G > 0 ? G : 0) * 0x100 +
    (B > 0 ? B : 0)
  ).toString(16).slice(1).toUpperCase();
};

